import React, { SyntheticEvent, useCallback, useState } from 'react';

import { css } from '@compiled/react';
import { FormattedMessage, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import Form, { Field, FormSection, FormFooter, ErrorMessage, Legend } from '@atlaskit/form';
import Heading from '@atlaskit/heading';
import { Flex, Stack, xcss } from '@atlaskit/primitives';
import TextField from '@atlaskit/textfield';
import { token } from '@atlaskit/tokens';

import { wacToken } from '@/libraries/design/foundations';

import { ANALYTICS_PREFIX } from '../../../common/constants';
import { PageProjectDetailsProps } from '../../../common/types';
import { useWizardConfiguration } from '../../../controllers';
import { useIsStandardizedOnboardingCtas } from '../../../hooks/experiment-hooks.ts';

import {
	JIRA_MAX_PROJECT_NAME_LENGTH,
	JIRA_MIN_PROJECT_NAME_LENGTH,
	PROJECT_NAME_TEXT_FIELD,
} from './constants';
import messages from './messages';
import { getTeamSpecificExamples } from './utils';

export const LeftHandSidePageProjectDetails: React.FC<PageProjectDetailsProps> = ({
	onClickNext,
	currentProjectName,
	onProjectNameChange,
	defaultProjectName,
	submitButton,
}) => {
	const { formatMessage } = useIntl();
	const { teamType } = useWizardConfiguration();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const isStandardizedCtas = useIsStandardizedOnboardingCtas();

	// State for managing selected example button
	const [selectedExampleIndex, setSelectedExampleIndex] = useState(0);
	const [hasClickedExample, setHasClickedExample] = useState(false);

	const handleOnSubmit = () => {
		const teamSpecificExamples = getTeamSpecificExamples(teamType);
		const selectedExampleText = teamSpecificExamples[selectedExampleIndex]?.defaultMessage;

		createAnalyticsEvent({
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: `${ANALYTICS_PREFIX}PageProjectDetailsSubmitButton`,
			eventType: 'ui',
			attributes: {
				teamType,
				hasClickedExample,
				selectedExampleIndex,
				selectedExampleText,
				submittedTextMatchesExample: currentProjectName === selectedExampleText,
			},
		}).fire();

		onClickNext();
	};

	const handleExampleClick = useCallback(
		(exampleText: string, index: number) => {
			setSelectedExampleIndex(index);
			setHasClickedExample(true);
			onProjectNameChange(exampleText);
		},
		[onProjectNameChange],
	);

	const validateProjectName = useCallback(
		(value: string | undefined) => {
			// If the project name is undefined, we don't need to validate it since we use the default project name
			if (value === undefined) {
				return;
			}
			const trimmedValueLength = value?.trim().length ?? 0;
			if (trimmedValueLength < JIRA_MIN_PROJECT_NAME_LENGTH) {
				return formatMessage(messages.projectNameMinCharacterLengthError, {
					_minCharacters: JIRA_MIN_PROJECT_NAME_LENGTH,
					get minCharacters() {
						return this['_minCharacters'];
					},
					set minCharacters(value) {
						this['_minCharacters'] = value;
					},
				});
			}
			if (trimmedValueLength > JIRA_MAX_PROJECT_NAME_LENGTH) {
				return formatMessage(messages.projectNameMaxCharacterLengthError, {
					maxCharacters: JIRA_MAX_PROJECT_NAME_LENGTH,
				});
			}
		},
		[formatMessage],
	);

	const handleProjectNameChanged = useCallback(
		(onChange: (e: SyntheticEvent<HTMLInputElement>) => void) =>
			(e: SyntheticEvent<HTMLInputElement>) => {
				onProjectNameChange(e.currentTarget.value);
				onChange(e);
			},
		[onProjectNameChange],
	);

	const FormContainer = FormSection;

	const renderHeading = () => {
		const subtext = (
			<p css={subtextStyles}>
				<FormattedMessage {...messages.bodyLineOne} />
			</p>
		);

		return (
			<Stack space="space.150">
				<div css={titleCss}>
					<Heading as="h1" size="large">
						{formatMessage(messages.spaceHeader)}
					</Heading>
				</div>
				{subtext}
			</Stack>
		);
	};

	const renderProjectExamples = () => {
		const exampleContent = (
			<Flex gap="space.100" wrap="wrap" justifyContent="start">
				{getTeamSpecificExamples(teamType).map((example, index) => {
					const exampleText = formatMessage(example);
					const isSelected = (currentProjectName ?? '').trim() === exampleText.trim();
					return (
						<Button
							key={teamType}
							appearance="subtle"
							isSelected={isSelected}
							aria-pressed={isSelected}
							onClick={() => handleExampleClick(exampleText, index)}
							css={[
								buttonStyles,
								isSelected && selectedButtonStyles,
								isSelected && selectedButtonTextStyles,
							]}
						>
							{exampleText}
						</Button>
					);
				})}
			</Flex>
		);

		return (
			<Stack space="space.0">
				<div css={exampleHeadingCss}>
					<Legend>{formatMessage(messages.projectExampleHeading)}</Legend>
				</div>
				{exampleContent}
			</Stack>
		);
	};

	const fieldLabel = formatMessage(messages.projectFormField);

	return (
		<Flex xcss={outerContainerStyles}>
			<Form onSubmit={handleOnSubmit}>
				{({ formProps }) => (
					<form {...formProps} noValidate>
						<Stack spread="space-between" xcss={containerStyles}>
							<Stack>
								{renderHeading()}
								<FormContainer>
									<Stack space="space.300">
										<div css={fieldLabelCss}>
											<Field
												name={PROJECT_NAME_TEXT_FIELD}
												label={fieldLabel}
												validate={validateProjectName}
											>
												{({ fieldProps, error, meta }) => (
													<>
														<TextField
															{...fieldProps}
															autoComplete="off"
															defaultValue={defaultProjectName}
															value={currentProjectName}
															onChange={handleProjectNameChanged(fieldProps.onChange)}
														/>
														{fieldProps.isInvalid && meta.touched && (
															<ErrorMessage>{error}</ErrorMessage>
														)}
													</>
												)}
											</Field>
										</div>
										{renderProjectExamples()}
									</Stack>
								</FormContainer>
							</Stack>
							<FormFooter>
								<div css={buttonGroupCss}>
									{submitButton ? (
										submitButton
									) : (
										<div css={submitButtonCss}>
											<Button type="submit" appearance="primary">
												{formatMessage(
													isStandardizedCtas ? messages.continueBtn : messages.getStartedBtn,
												)}
											</Button>
										</div>
									)}
								</div>
							</FormFooter>
						</Stack>
					</form>
				)}
			</Form>
		</Flex>
	);
};

const outerContainerStyles = xcss({
	height: '100%',
});
const containerStyles = xcss({
	height: '100%',
});

const buttonStyles = css({
	borderWidth: '1px !important',
	// eslint-disable-next-line local/ensure-wac-token-usage
	borderColor: '#BCBDC0 !important',
	borderRadius: '6px !important',
	borderStyle: 'solid !important',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontSize: '14px !important',
		fontWeight: `${token('font.weight.regular')} !important`,
		color: `${token('color.text.subtle')} !important`,
	},
});

const selectedButtonStyles = css({
	borderColor: `${token('color.border.focused')} !important`,
});

const selectedButtonTextStyles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		color: `${token('color.text.selected')} !important`,
	},
});

const titleCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	h1: {
		fontFamily: wacToken('font.charlie.display'),
		fontWeight: 700,
		fontSize: '24px',
		lineHeight: '28px',
	},
});

const subtextStyles = css({
	font: token('font.body'),
	color: token('color.text.subtle'),
	margin: 0,
});

const exampleHeadingCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	legend: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
});

const submitButtonCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	button: {
		borderRadius: '6px !important',
		paddingBlock: '6px !important',
		paddingInline: '12px !important',
		margin: '0 !important',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	span: {
		fontFamily: `var(--atlassian-sans-font-family) !important`,
		fontSize: '14px !important',
		lineHeight: '1.5rem !important',
		fontWeight: `${token('font.weight.medium')} !important`,
		textAlign: 'center',
		margin: '0 !important',
	},
});

const fieldLabelCss = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	label: {
		font: token('font.body.small'),
		fontWeight: token('font.weight.medium'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	input: {
		font: token('font.body'),
		color: token('color.text.subtlest'),
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	div: {
		borderColor: `${token('color.border.input')} !important`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]']: {
		borderRadius: '6px',
		borderWidth: '1px',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
	['div[data-ds--text-field--container]:focus-within']: {
		borderWidth: '2px',
	},
});

const buttonGroupCss = css({
	display: 'flex',
	gap: token('space.100'),
});
